import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Users, Square } from "@phosphor-icons/react";

// Map legacy statuses to new workflow statuses
const normalizeStatus = (status) => {
  switch (status) {
    case "available": return "open";
    case "occupied": return "seated";
    case "needs_attention": return "bussing";
    case "reserved": return "reserved"; // kept for reservations flow
    default: return status || "open";
  }
};

// Display label + color palette for each status
// Each entry: { label, bg, border, text, dot }
const STATUS_STYLES = {
  open:     { label: "Open",     bg: "#FFFFFF", border: "#C9C5BD", text: "#5C5852" },
  seated:   { label: "Seated",   bg: "#E8EEF9", border: "#4A6FA5", text: "#1E3A66" },
  ordered:  { label: "Ordered",  bg: "#F6EBD9", border: "#D29856", text: "#7A5424" },
  served:   { label: "Served",   bg: "#ECF1EA", border: "#728A6D", text: "#3A4D37" },
  bussing:  { label: "Bussing",  bg: "#F5E3DE", border: "#B85D43", text: "#7A2F1B" },
  reserved: { label: "Reserved", bg: "#EFE6F5", border: "#8E6CB3", text: "#432A66" },
};

const getStyle = (status) => STATUS_STYLES[normalizeStatus(status)] || STATUS_STYLES.open;

const FloorPlan = ({ tables, selectedTable, onTableSelect, onTableMove, onSetStatus, canOverride = false, compact = false, draggable = false }) => {
  const containerRef = useRef(null);
  const [draggedTable, setDraggedTable] = useState(null);

  const getTableSize = (shape, seats, scale = 1.0) => {
    // Match the editor's zoom level (base 70) so the POS floor plan stays
    // zoomed out enough to place tables freely.
    const baseSize = (compact ? 64 : 70) * (scale || 1);
    const seatMultiplier = Math.min(seats / 4, 1.5);
    switch (shape) {
      // Round tables share the rectangle's long-side dimension so the table
      // name + status badge + seat count all fit comfortably inside the circle.
      case "round": return { width: baseSize * 1.5 * seatMultiplier, height: baseSize * 1.5 * seatMultiplier };
      case "rectangle": return { width: baseSize * 1.5 * seatMultiplier, height: baseSize * seatMultiplier };
      default: return { width: baseSize * seatMultiplier, height: baseSize * seatMultiplier };
    }
  };

  const getShapeClass = (shape) => shape === "round" ? "rounded-full" : "rounded-lg";

  // Inner canvas expands beyond the visible viewport so a restaurant with
  // many tables (or one placed near the edge) can scroll to reach them.
  // Grow to fit the furthest table + a buffer.
  const canvasMinWidth = 1600;
  const canvasMinHeight = 1200;
  const canvasWidth = tables.reduce(
    (max, t) => {
      const size = getTableSize(t.shape, t.seats, t.size_scale);
      return Math.max(max, (t.position?.x || 100) + size.width + 200);
    },
    canvasMinWidth
  );
  const canvasHeight = tables.reduce(
    (max, t) => {
      const size = getTableSize(t.shape, t.seats, t.size_scale);
      return Math.max(max, (t.position?.y || 100) + size.height + 200);
    },
    canvasMinHeight
  );

  const handleDragEnd = (e, table, info) => {
    setDraggedTable(null);
    if (containerRef.current) {
      // containerRef is the inner canvas; info.point is in viewport coords.
      // getBoundingClientRect() returns the canvas position accounting for
      // scroll, so subtracting it gives the correct canvas-space coordinate.
      // Clamp with a generous overflow so the user can drag past the current
      // canvas edge in one motion — the canvas re-computes wider on the next
      // render to fit the new position + buffer.
      const rect = containerRef.current.getBoundingClientRect();
      const size = getTableSize(table.shape, table.seats, table.size_scale);
      const slack = 500;
      const newX = Math.max(0, Math.min(info.point.x - rect.left - size.width / 2, canvasWidth + slack - size.width));
      const newY = Math.max(0, Math.min(info.point.y - rect.top - size.height / 2, canvasHeight + slack - size.height));
      onTableMove(table.id, { x: newX, y: newY });
    }
  };

  // Click handler: if a table is in 'bussing' status, clicking it resets to 'open'.
  // Otherwise it proceeds with the normal table-select behaviour.
  const handleTableClick = (table) => {
    if (onSetStatus && normalizeStatus(table.status) === "bussing") {
      onSetStatus(table.id, "open");
      return;
    }
    onTableSelect(table);
  };

  const StatusBadge = ({ style, small = false }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium uppercase tracking-wide ${small ? "text-[8px] px-1.5 py-[1px]" : "text-[9px] px-2 py-[2px]"}`}
      style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}
    >
      <span className="w-1 h-1 rounded-full" style={{ backgroundColor: style.border }} />
      {style.label}
    </span>
  );

  // Legend shared between mobile + desktop
  const Legend = ({ dense = false }) => (
    <div className={`flex items-center ${dense ? "gap-2" : "gap-3"} flex-wrap`} data-testid="table-legend">
      {Object.entries(STATUS_STYLES).filter(([k]) => k !== "reserved").map(([key, s]) => (
        <div key={key} className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full border" style={{ backgroundColor: s.bg, borderColor: s.border }} />
          <span className="text-[10px] text-muted-foreground">{s.label}</span>
        </div>
      ))}
    </div>
  );

  // Mobile compact: grid layout so all tables fit on screen
  if (compact) {
    return (
      <div className="bg-white rounded-2xl border border-border p-3" data-testid="floor-plan">
        <div className="grid grid-cols-3 gap-3">
          {tables.map((table, index) => {
            const isSelected = selectedTable?.id === table.id;
            const isRound = table.shape === "round";
            const style = getStyle(table.status);
            return (
              <motion.button
                key={table.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15, delay: index * 0.03 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleTableClick(table)}
                className={`aspect-square flex flex-col items-center justify-center gap-1 px-1 ${isRound ? "rounded-full" : "rounded-xl"} border-2 transition-shadow ${isSelected ? "ring-2 ring-primary ring-offset-2 shadow-md" : "shadow-sm"} group relative`}
                style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}
                data-testid={`table-${table.id}`}
              >
                <span className="font-heading font-medium text-sm leading-tight">{table.name}</span>
                <StatusBadge style={style} small />
                <div className="flex items-center gap-1 text-[10px] opacity-75">
                  <Users size={10} weight="bold" /><span>{table.seats}</span>
                </div>
                {canOverride && onSetStatus && normalizeStatus(table.status) !== "open" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Reset ${table.name} to Open?`)) {
                        onSetStatus(table.id, "open");
                      }
                    }}
                    className="absolute top-1 right-1 px-1 py-px rounded-full bg-card border border-primary/30 text-[8px] uppercase font-medium text-primary"
                    title="Manager override: reset to Open"
                    data-testid={`override-open-${table.id}`}
                  >
                    Reset
                  </button>
                )}
              </motion.button>
            );
          })}
        </div>

        {tables.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <div className="text-center">
              <Square size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No tables configured</p>
            </div>
          </div>
        )}

        <div className="mt-3 pt-2 border-t border-border flex items-center justify-center">
          <Legend dense />
        </div>
      </div>
    );
  }

  // Desktop: absolute positioned draggable layout inside a scrollable viewport
  return (
    <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid="floor-plan">
      <div className="overflow-auto" style={{ height: 600 }} data-testid="floor-plan-viewport">
        <div
          ref={containerRef}
          className="relative floor-grid"
          style={{ width: canvasWidth, height: canvasHeight }}
          data-testid="floor-plan-canvas"
        >
          {tables.map((table, index) => {
            const size = getTableSize(table.shape, table.seats, table.size_scale);
            const isSelected = selectedTable?.id === table.id;
            const isDragging = draggedTable === table.id;
            const style = getStyle(table.status);

            return (
              <motion.div
                key={table.id}
                drag={draggable}
                dragMomentum={false}
                dragElastic={0}
                onDragStart={() => draggable && setDraggedTable(table.id)}
                onDragEnd={(e, info) => draggable && handleDragEnd(e, table, info)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: isDragging ? 1.05 : 1, x: table.position?.x || 100, y: table.position?.y || 100 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleTableClick(table)}
                className={`absolute cursor-pointer select-none ${getShapeClass(table.shape)} border-2 flex flex-col items-center justify-center gap-1 px-1 transition-shadow duration-200 group ${isSelected ? "ring-2 ring-primary ring-offset-2" : ""} ${isDragging ? "shadow-xl z-50" : "shadow-sm hover:shadow-md"}`}
                style={{ width: size.width, height: size.height, left: 0, top: 0, backgroundColor: style.bg, borderColor: style.border, color: style.text }}
                data-testid={`table-${table.id}`}
              >
                <span className="font-heading font-medium text-sm leading-none">{table.name}</span>
                <StatusBadge style={style} />
                <div className="flex items-center gap-1 text-[10px] opacity-75">
                  <Users size={11} weight="bold" /><span>{table.seats}</span>
                </div>
                {canOverride && onSetStatus && normalizeStatus(table.status) !== "open" && (
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Reset ${table.name} to Open? This will override the current "${style.label}" status.`)) {
                        onSetStatus(table.id, "open");
                      }
                    }}
                    className="absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full bg-card border border-primary/30 text-[9px] font-medium uppercase tracking-wide text-primary hover:bg-primary hover:text-primary-foreground shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Manager override: reset to Open"
                    data-testid={`override-open-${table.id}`}
                  >
                    Reset
                  </button>
                )}
              </motion.div>
            );
          })}

          {tables.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Square size={48} className="mx-auto mb-2 opacity-30" />
                <p>No tables configured</p>
                <p className="text-sm">Add tables in Settings</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border px-3 py-2 bg-white/85">
        <Legend />
      </div>
    </div>
  );
};

export default FloorPlan;
